import {
  useEffect, useMemo, useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import * as S from './FormProductsList.styles'
import { RestockingListTypesEnum, SourceType, ShedCode } from '../../RestockingTypes'
import { actions, initialState } from 'src/features/restocking/RestockingState'
import { Typography } from '@mui/material'
import { toast } from 'react-toastify'
import { fixInputSx } from '../../utils'

const shedCode = [ShedCode.CF, ShedCode.CR, ShedCode.CT]
const restockingSubType = [SourceType.PROGRAMMED, SourceType.DEMAND, SourceType.TOTAL]

interface Props {
  idRestockingType: number
  setShedCode: (value: string[]) => void
  setRestockingSubType: (value: string) => void
}

export const FormProductsList = ({ idRestockingType, setShedCode, setRestockingSubType }: Props) => {
  const {
    register, handleSubmit, getValues, clearErrors, formState: { errors },
  } = useForm()
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvFileAlreadySended, setCsvFileAlreadySended] = useState(false)
  const [csvFileErrors, setCsvFileErrors] = useState<string>('')

  const dispatch = useDispatch()

  const { fc, validateFc } = useFulfillmentCenter()

  useEffect(() => {
    validateFc()
  }, [validateFc])

  const shedCodeMap = {
    'Câmara Fria - CF': 'CF',
    'Câmara Refrigerada - CR': 'CR',
    'Container - CT': 'CT',
  }

  const restockingSubTypeMap = {
    0: 'PROGRAMADA',
    1: 'DEMANDA',
    2: 'TOTAL',
  }

  const shedCodeOptions = useMemo(() => shedCode.map((shed, index) => ({
    description: shed,
    id: index,
  })), [])

  const restockingSubTypeOptions = useMemo(() => restockingSubType.map((type, index) => ({
    description: type,
    id: index,
  })), [])

  const handleFileChange = (event: any) => {
    const fileInput = event?.target?.files?.[0] as File
    setCsvFileAlreadySended(false)
    setCsvFileErrors('')
    if (fileInput && fileInput.type !== 'text/csv') {
      setCsvFile(null)
      toast.error('Selecione um arquivo ".CSV"!')
      return
    }
    setCsvFile(fileInput)
  }

  const handleFileDetach = () => {
    setCsvFile(null)
    setCsvFileErrors('')
  }

  const onSubmitFresh = () => {
    const restockingSubTypeSelected = getValues('restockingSubType')
    setRestockingSubType(restockingSubTypeMap[restockingSubTypeSelected])

    const selectedCodes = getValues('shedCode').map((option: string) => shedCodeMap[option])

    setShedCode(selectedCodes)
    dispatch(actions.getProductsList({
      fulfillmentCenterId: fc.id,
      shedCode: selectedCodes,
      sourceType: restockingSubTypeMap[restockingSubTypeSelected],
    }))
  }

  const onSubmitGrocery = (event: any) => {
    event.preventDefault()
    if (csvFile) {
      if (csvFileAlreadySended) {
        toast.error('Arquivo já enviado, por segurança, selecione-o novamente!')
        return
      }
      dispatch(actions.getProductsListByCSV({
        csvFile, idType: idRestockingType, setCsvFileAlreadySended, setCsvFileErrors,
      }))
    }
  }

  useEffect(() => {
    dispatch(actions.setProductsList({ productsList: initialState.productsList }))
    setCsvFile(null)
    setCsvFileErrors('')
    setShedCode([])
    setRestockingSubType('')
    clearErrors('shedCode')
    clearErrors('restockingSubType')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idRestockingType])

  function getDownloadDirectory(restockingListType: RestockingListTypesEnum): string {
    const directories = {
      [RestockingListTypesEnum.RESTOCKING_GROCERY]: '/restocking_template.csv',
      [RestockingListTypesEnum.RESTOCKING_FRESH]: '/restocking_template.csv',
      [RestockingListTypesEnum.MISSING_GROCERY]: '/missing_picking_template.csv',
      [RestockingListTypesEnum.MISSING_FRESH]: '/missing_picking_template.csv',
      [RestockingListTypesEnum.TRANSFER]: '/transfer_movement_template.csv',
      [RestockingListTypesEnum.TRANSFER_LOSS_PREVENTION]: '/transfer_movement_template.csv',
    }

    const directory = directories[restockingListType]

    if (!directory) {
      return '/generic_movement_template.csv'
    }

    return directory
  }

  const systemForm = () => (
    <form onSubmit={handleSubmit(onSubmitFresh)} data-testid='form-products-list-test-id'>
      <S.FilterContainer>
        <S.CustomSelectWrapper>
          <S.CustomMultipleSelect
            formControlSx={fixInputSx}
            register={register}
            inputName='shedCode'
            label='Piso de excedente'
            errors={errors.shedCode}
            options={shedCodeOptions}
            dataTestId='select-shed-code-test'
            id='shedCode'
            required
            size='medium'
            fullWidth
            clearInputOnChange={idRestockingType}
          />
          <S.CustomSelect
            formControlSx={fixInputSx}
            label='Subtipo de reposição'
            autoComplete='off'
            {...register('restockingSubType', {
              required: 'Campo obrigatório',
            })}
            id='restockingSubType'
            options={restockingSubTypeOptions}
            error={!!errors.restockingSubType}
            helperText={errors.restockingSubType?.message}
            variant='outlined'
            dataTestId='select-restocking-sub-type-test'
            size='medium'
            type='text'
            optionNone={false}
            fullWidth
          />
        </S.CustomSelectWrapper>
        <S.CustomButton
          title='Buscar'
          type='submit'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
        />
      </S.FilterContainer>
    </form>
  )

  const csvForm = () => (
    <form onSubmit={onSubmitGrocery} data-testid='form-products-list-test-id'>
      <S.FilterContainer>
        <S.CustomSelectWrapper>
          <S.SelectFileContainer>
            {csvFile
              && (
              <S.FileContainer>
                <Typography alignSelf='center'>{csvFile?.name}</Typography>
                <S.CustomButtonDetach
                  id='csv-file-detach'
                  name='csv-file-detach'
                  data-testid='csv-file-detach-test'
                  onClick={handleFileDetach}
                >
                  X
                </S.CustomButtonDetach>
              </S.FileContainer>
              )}
            <S.CustomButtonFileSelect
              sx={{ width: '16rem', minWidth: '16rem' }}
              id='csv-file-select'
              name='csv-file-select'
              title='Importar Arquivo CSV'
              bgColor='blue'
              hoverColor='darkBlueHover'
              letterColor='white'
              testId='csv-file-select-test'
              onChange={handleFileChange}
              accept='.csv'
            />
          </S.SelectFileContainer>
        </S.CustomSelectWrapper>
        <S.CustomButton
          title='Enviar'
          type='submit'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          disabled={!csvFile}
        />
        <S.CustomButtonDownloadTemplate
          title='Baixar Template'
          bgColor='confirm'
          hoverColor='confirmHover'
          letterColor='white'
          href={getDownloadDirectory(idRestockingType)}
          testId='csv-file-download-template-test-id'
        />
      </S.FilterContainer>
      {csvFileErrors.length > 0
      && (
      <S.ErrorsMainContainer>
        <S.ErrorsSubContainer>
          <Typography color='darkred'>Linhas com erros:</Typography>
          <S.ErrorsContainer>
            <Typography color='darkred'>{csvFileErrors}</Typography>
          </S.ErrorsContainer>
        </S.ErrorsSubContainer>
      </S.ErrorsMainContainer>
      )}
    </form>
  )

  const form = () => {
    if (idRestockingType === RestockingListTypesEnum.RESTOCKING_FRESH_AUTOMATIC) {
      return systemForm()
    }
    return csvForm()
  }

  return (
    form()
  )
}
